import React from 'react';
import CloseRounded from '@mui/icons-material/CloseRounded';
import GlobalStyles from '@mui/material/GlobalStyles';
import { Box, IconButton, Modal as MuiModal, Typography } from '@mui/material';

import { modalRootCenteredSx, safeAreaInset } from '../platform/safeArea';

/** Google Places appends `.pac-container` to `body`. Modal scroll-lock + focus trap break it — fix z-index and opt out when using Places. */
const placesAutocompleteGlobalStyles = (
  <GlobalStyles
    styles={{
      '.pac-container': {
        zIndex: '2147483647 !important',
      },
    }}
  />
);

/**
 * @param {{ bleed?: boolean }} props.bleed - Fullscreen only: skip the title/close header row and all
 * padding so content (e.g. an edge-to-edge map) can fill the entire modal. The content is then
 * responsible for its own safe-area padding and for providing a way back (e.g. a back button).
 * @param {{ maxWidth?: number }} props.maxWidth - Caps the modal width and centers it horizontally.
 * Applies to both centered dialogs and `fullScreen` (the latter stays full-height, but on a wide
 * desktop viewport it's letterboxed to this width instead of stretching edge-to-edge).
 */
export default function Modal({ open, onClose, fullScreen, bleed, children, title, maxWidth = 440 }) {
  const showHeader = !bleed;
  return (
    <>
      {open ? placesAutocompleteGlobalStyles : null}
      <MuiModal
        open={open}
        onClose={onClose}
        sx={fullScreen ? undefined : modalRootCenteredSx}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            boxShadow: 24,
            outline: 0,
            overflow: bleed ? 'hidden' : 'auto',
            boxSizing: 'border-box',
            ...(fullScreen
              ? {
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '100%',
                  maxWidth,
                  height: '100%',
                  maxHeight: '100%',
                  borderRadius: 0,
                  my: 0,
                  ...(bleed
                    ? { p: 0 }
                    : {
                        pt: `calc(12px + ${safeAreaInset.top})`,
                        pb: `calc(16px + ${safeAreaInset.bottom})`,
                        pl: `calc(16px + ${safeAreaInset.left})`,
                        pr: `calc(16px + ${safeAreaInset.right})`,
                      }),
                }
              : {
                  position: 'relative',
                  width: '100%',
                  maxWidth,
                  maxHeight: '100%',
                  borderRadius: 2,
                  my: 'auto',
                  mx: 'auto',
                  p: 2,
                  pt: 1.5,
                }),
          }}
        >
          {showHeader ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1,
                mb: 1.5,
              }}
            >
              {title ? (
                <Typography variant="h6" component="h2" sx={{ pr: 1, lineHeight: 1.3, flex: 1 }}>
                  {title}
                </Typography>
              ) : (
                <Box sx={{ flex: 1 }} />
              )}
              {onClose ? (
                <IconButton
                  aria-label="Close"
                  onClick={onClose}
                  size="small"
                  sx={{ mt: -0.5, mr: -0.5, color: 'text.secondary' }}
                >
                  <CloseRounded />
                </IconButton>
              ) : null}
            </Box>
          ) : null}
          {children}
        </Box>
      </MuiModal>
    </>
  );
}
